<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">GL Account Templates</h1>
    <span class="banner py-3 mb-4">
      <div>The purpose of a GL Account Template is to group your GL accounts into budget groups and to customize the accounts available for budgeting. You will be able to add additional GL accounts from the balance sheet, remove GL accounts that will not be budgeted, or move GL accounts to a different group.</div>
      <br />
      <div>A default budget template will be created by for you by the system called ResMan Default Budget Template, although you may choose to edit how it functions. While most companies will use the same template for all properties, for all years, you do have the option of creating additional Templates.</div>
      <br />
      <div>The purpose to creating a second template would be that it either has different groupings of accounts or includes different GL accounts.</div>
      <br />
      <div>Expand any of the topics below for more information on that step.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Creating a GL Account Budget Template</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Initially you will have a Budget Template named "<strong>ResMan Default Budget Template</strong>" that will replicate the grouping of your chart of accounts. If needed, you can edit that template, or create a new template.</div>
          <div>To create a budget template, go to Accounting > BudgetsPro > Templates. You can either use the <strong>Copy</strong> feature within the ellipsis (3 dots) on an existing template, or you can select <strong>+New Template</strong>.</div>
          <div>Enter the name of the template and select which properties will have access to this template.  To select the properties, click on the names on the left-side pane. They will be highlighted. Then click the ADD option in the middle. They will transfer to the right-side or Selected pane.</div>
          <div>Once you have completed your selections, click <strong>CREATE TEMPLATE</strong>.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Budget Group Type</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>A budget group type is a grouping of GL accounts that are kept together in a defined section within the budget. There are four types of groups in BudgetsPro:</div>
          <ul>
            <li>Income (GL accounts that are type "Income")</li>
            <li>Expense (GL accounts that are type "Expense")</li>
            <li>Non-Operating Expense (GL accounts that are type "Non-Operating Expense")</li>
            <li>Other Accounts (Any other GL accounts added manually)</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Budget Groups</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Budget groups will be created for you when a template is created. We recommend that you use the <strong>ResMan Default Budget Template</strong> as it will most closely resemble your financial statement format. However, you can use any template.</div>
          <div>You may wish to add additional groups of GL Accounts to your template. An example is that you may wish to create a group called Capital Accounts that consists of balance sheet GL accounts.</div>
          <div>If you create a new budget template, the system will create these groups for you, but you can edit these as needed.</div>
          <br />
          <table>
            <thead>
              <tr>
                <th>System Budget Groups</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Rental Income</td>
                <td>Income</td>
                <td>The Default GL accounts for Gross Potential Rent, Gain/Loss to Lease and Vacancy Loss</td>
              </tr>
              <tr>
                <td>Other Income</td>
                <td>Income</td>
                <td>All Income or Other Income GL accounts, except for Gross Potential Rent, Gain/Loss to Lease and Vacancy Loss</td>
              </tr>
              <tr>
                <td>Management Fees</td>
                <td>Expense</td>
                <td>The default GL accounts for Management Fees</td>
              </tr>
              <tr>
                <td>Turnover Costs</td>
                <td>Expense</td>
                <td>No GL accounts are automatically added to the Turnover Costs system budget group. Any expense GL accounts can be added to this system budget group</td>
              </tr>
              <tr>
                <td>Other Expenses</td>
                <td>Expense</td>
                <td>All remaining expense GL accounts</td>
              </tr>
              <tr>
                <td>Excluded Accounts</td>
                <td>N/A</td>
                <td>The Excluded Accounts group allows a user to remove a GL account from the budget. Any GL account in this budget group will not display and will be excluded from the budget.</td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Custom Budget Groups</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>A custom budget group is a user defined budget group in a specific template. A custom budget group can be created and be associated to an Income, Expense, Non-Operating Expense or Other Accounts budget type.</div>
          <div>Once a custom budget group is created, GL accounts can be added to the custom budget group and the custom budget group displays in the budget template and in the budget.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Creating a Custom Budget Group</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>To create a custom budget group, go to Accounting > BudgetsPro > Templates. Click the edit icon on the right of a template. Select <strong>+Budget Group</strong>.</div>
          <div>Select Budget Group Type of Income, Expense, Non-Operating Expenses or Other Accounts and name the new custom budget group. Add GL accounts to the custom budget group by clicking on a GL account. The GL accounts that are in the "Selected" pane are the GL accounts that are part of the custom budget group.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Edit a Budget Group</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If needed, the Group name and type can be changed. In addition, you can also change the GL Accounts that are part of this Group.</div>
          <div>To edit a budget group, go to Accounting > BudgetsPro > Templates. Click the edit icon on the right of a template. Click the edit icon on the right of a GL Group. The page will load, and the budget type, name, and GL Accounts can be edited.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Templates')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'TemplatesHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  th,td {
      padding:10px;
      border: 1px solid var(--v-resGrey-lighten1);
  }
  .no-bullet {
    list-style-type: none;
  }
  .icon {
    order: 0;
  }

  .header {
    order: 1;
  }

  .help-container {
    border-bottom: 1px solid var(--v-resGrey-lighten1)
  }

  .help-content div,
  .help-content li {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .pointer {
    cursor: pointer !important;
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }

  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

  .separator:not(:empty)::after {
    margin-left: .25em;
  }
</style>
